import { Capacitor } from "@capacitor/core";
import Analytics from "../../services/analytics";
import Constants from "../../services/constants";
import * as GoogleTagManagerActions from "./gtm-actions";
// plugins
import { FireBaseHelper } from "../../plugins/FireBaseHelperPlugin";
// types
import type { RouteChanges, ActionList, EventList, HbDataLayerData } from "../../types/analytics";
import type { Platform } from "../../types/misc";

const routeChanges = [
	{
		location: `^/history/(${Object.values(Constants.Tabs.History).join("|")})`, // TODO: end with `$`?
		action: GoogleTagManagerActions.acHistory,
	},
	{
		location: "^/account$",
		action: GoogleTagManagerActions.acAccount,
	},
	{
		location: "^/help", // TODO: end with `$`?
		action: GoogleTagManagerActions.acSupport,
	},
	{
		location: "^/about$",
		action: GoogleTagManagerActions.acAbout,
	},
	{
		location: "^/about/userterms$",
		action: GoogleTagManagerActions.acAboutUserTerms,
	},
	{
		location: "^/about/imprint$",
		action: GoogleTagManagerActions.acAboutImprint,
	},
	{
		location: "^/add/home", // TODO: end with `$`?
		action: GoogleTagManagerActions.acAddGateway,
	},
	{
		location: `^/add/device/pairing/(${Object.values(Constants.PairingCategoryType).join("|")})`, // TODO: end with `$`?
		action: GoogleTagManagerActions.acAddDevice,
	},
	{
		location: "^/add/group$",
		action: GoogleTagManagerActions.acAddGroup,
	},
	{
		location: "^/add/rule", // TODO: end with `$`?
		action: GoogleTagManagerActions.acAddRule,
	},
	{
		location: "^/explore/[A-Z]{2}$",
		action: GoogleTagManagerActions.acExplorePage,
	},
] as const satisfies RouteChanges;

const actionList = [
	{
		/** @deprecated Old quickview doesn't exist any more */
		name: "openQuickView",
		action: (data) => (GoogleTagManagerActions.acGatewaySituation(data.smartMode, data.deviceGroupsCount, data.deviceGroupNames, data.devicesPerGroup)),
	},
	{
		name: "openDevices",
		action: (data) => (GoogleTagManagerActions.acDevices(data.deviceCategoriesCount, data.deviceCategoryNames, data.devicesPerCategory)),
	},
	{
		name: "openDeviceDetails",
		action: (data) => (GoogleTagManagerActions.acDeviceDetails(data.deviceName)),
	},
	{
		name: "openDeviceDiagram",
		action: (data) => (GoogleTagManagerActions.acDeviceMeasuredValues(data.clusterId, data.deviceName)),
	},
	{
		name: "openRules",
		action: (data) => (GoogleTagManagerActions.acRules(data.rulesCount)),
	},
	{
		name: "openRuleDetailsTemplate",
		action: (data) => (GoogleTagManagerActions.acRuleDetailsTemplate(data.ruleName)),
	},
	{
		name: "openDetailsTimeSwitchDiagram",
		action: (data) => (GoogleTagManagerActions.acRuleDetailsTimeSwitchingDiagram(data.deviceName, data.ruleName)),
	},
	{
		name: "errorMessage",
		action: (data) => (GoogleTagManagerActions.acError(data.errorMessage)),
	},
] as const satisfies ActionList;

const eventList = [
	{
		name: "smartModeChange",
		event: (data) => (GoogleTagManagerActions.evGatewaySituation(data.smartMode)),
	},
	{ // TODO: unused
		name: "deviceUpdate",
		event: (data) => (GoogleTagManagerActions.evDeviceUpdate(data.deviceName)),
	},
	{
		name: "ruleTabChange",
		event: (data) => (GoogleTagManagerActions.evRulesTab(data.ruleTab)),
	},
	{
		name: "eventSmartModesChange",
		event: (data) => (GoogleTagManagerActions.evGatewaySituationChange(data.smartModes)),
	},
	{
		name: "historyTabChange",
		event: (data) => (GoogleTagManagerActions.evHistoryTab(data.historyTab)),
	},
	{
		name: "historyFilterChange",
		event: (data) => (GoogleTagManagerActions.evHistoryFilter(data.historyFilterCount)),
	},
	{
		name: "deleteAccount",
		event: () => (GoogleTagManagerActions.evAccountDelete()),
	},
	{
		name: "zWaveModeChange",
		event: (data) => (GoogleTagManagerActions.evZWaveMode(data.zwaveMode)),
	},
	{
		name: "addGateway",
		event: () => (GoogleTagManagerActions.evAddGateway()),
	},
	{
		name: "addDevice",
		event: (data) => (GoogleTagManagerActions.evAddDevice(data.deviceCategoryName, data.deviceName)),
	},
	{
		name: "addGroup",
		event: (data) => (GoogleTagManagerActions.evAddGroup(data.devicesCount)),
	},
	{
		name: "addRule",
		event: (data) => (GoogleTagManagerActions.evAddRule(data.smartModes, data.ruleName)),
	},
	{
		name: "exploreCarouselView",
		event: (data) => (GoogleTagManagerActions.evExploreCarouselView(data.text, data.position)),
	},
	{
		name: "exploreCarouselClick",
		event: (data) => (GoogleTagManagerActions.evExploreCarouselClick(data.text, data.position)),
	},
	{
		name: "exploreBrandsInteraction",
		event: (data) => {
			GoogleTagManagerActions.evExploreClick(data.text); // added this, to make the brands also visible in ther "Partner section" of our GTM explore report
			GoogleTagManagerActions.evExploreBrandsInteraction(data.text, data.interactionType, data.position);
		},
	},
	{
		name: "exploreClick",
		event: (data) => (GoogleTagManagerActions.evExploreClick(data.text)),
	},
	{
		name: "exploreChangeCountry",
		event: (data) => (GoogleTagManagerActions.evExploreChangeCountry(data.countryName)),
	},
] as const satisfies EventList;

class AnalyticsHornbach extends Analytics {

	constructor() {
		super();

		if (window.hbDataLayer && window.dataLayer) {
			this.routeChanges = routeChanges;
			this.actionList = actionList;
			this.eventList = eventList;
			const platform = Capacitor.getPlatform() as Platform;
			window.hbDataLayer.app_platform = (platform === "web") ? platform : `${platform} app`;
		}
	}

	public override emitFirstLoad(hbDataLayerData: HbDataLayerData): void {
		if (window.hbDataLayer) {
			GoogleTagManagerActions.updateHbDataLayer(hbDataLayerData);
		}

		if (window.dataLayer) {
			GoogleTagManagerActions.sendFirstLoad();
		}
	}

	public override emitRouteChange(location: string): void {
		super.emitRouteChange(location);
		// track every route change as screen view in Firebase Analytics (only on native, web is tracked via GTM)
		// we disabled automatic screen reporting in native Firebase config, because it doesn't work well
		// with React Router and would lead to inaccurate screen view data.
		// So we log screen views manually here on every route change.
		if (Capacitor.isNativePlatform()) {
			FireBaseHelper.logScreenView({ screenName: location }).catch((error) => {
				console.error("Failed to log screen view to Firebase:", error);
			});
		}
	}

}

export default (new AnalyticsHornbach());
